import { useQuery } from "@tanstack/react-query";
import { Boxes, Package, PackageCheck, Warehouse, History, BellRing } from "lucide-react";
import { get } from "@/lib/api";
import { fmtNum } from "@/lib/format";
import { Panel } from "@/components/common/Panel";
import { KpiTile } from "@/components/common/KpiTile";
import { LoadingState, EmptyState } from "@/components/common/States";

/** AUDIT INV V2 §16 — Tableau de bord Inventaire V1 (W1).
 *  5 KPI + Top 5 produits + Répartition par entrepôt + 5 derniers mouvements + placeholder alertes V2.1.
 *  Jamais d'addition cross-unités : chaque unité (kg/pcs/L) est présentée séparément. */
export const TableauBordInventaire = () => {
  const q = useQuery({ queryKey: ["inv-tableau-bord"], queryFn: () => get("/inventaire/tableau-bord"),
    refetchInterval: 60000 });

  if (q.isLoading) return <LoadingState rows={4} />;
  if (q.error) return <EmptyState title="Erreur de chargement" hint={q.error.message} />;
  const d = q.data || {};
  const unites = d.par_unite || [];
  const top = d.top_produits || [];
  const ventil = d.ventilation_par_entrepot || [];
  const derniers = d.derniers_mouvements || [];

  // Grouper la ventilation par entrepôt pour un affichage synthétique multi-unités.
  const ventilParEntrepot = {};
  ventil.forEach((v) => {
    if (!ventilParEntrepot[v.entrepot]) ventilParEntrepot[v.entrepot] = [];
    ventilParEntrepot[v.entrepot].push(v);
  });

  return (
    <div data-testid="inv-tableau-bord" className="space-y-3">
      {/* ============ Rangée KPI ============ */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
        <KpiTile testId="kpi-tdb-produits" label="Produits en stock" value={fmtNum(d.nb_produits_en_stock)} icon={Package} />
        <KpiTile testId="kpi-tdb-lots" label="Lots suivis" value={fmtNum(d.nb_lots)} icon={PackageCheck} />
        {unites.slice(0, 2).map((u) => (
          <KpiTile key={u.unite} testId={`kpi-tdb-stock-${u.unite}`}
            label={`Stock physique (${u.unite})`} value={fmtNum(u.physique)} unit={u.unite} icon={Boxes}
            sub={`Disponible ${fmtNum(u.disponible)}`} />
        ))}
        <KpiTile testId="kpi-tdb-entrepots" label="Entrepôts actifs" value={fmtNum(Object.keys(ventilParEntrepot).length)} icon={Warehouse}
          sub="voir Configuration" />
      </div>

      {unites.length > 2 && (
        <div data-testid="tdb-autres-unites" className="panel p-3 flex flex-wrap gap-3 text-[11px]">
          <span className="eyebrow">Autres unités :</span>
          {unites.slice(2).map((u) => (
            <span key={u.unite} className="font-mono">{u.unite} : <b>{fmtNum(u.physique)}</b> (dispo {fmtNum(u.disponible)})</span>
          ))}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {/* ============ Top 5 produits ============ */}
        <Panel title="Top 5 produits par stock" testId="panel-top-produits" bodyClassName="p-3">
          {top.length === 0 ? <EmptyState compact title="Aucun stock" /> : (
            <div className="space-y-1.5">
              {top.map((t, i) => (
                <div key={i} data-testid={`top-produit-${i}`} className="flex items-center justify-between text-xs">
                  <span className="font-mono text-muted-foreground w-6">#{i + 1}</span>
                  <span className="flex-1 font-mono">{t.produit}</span>
                  <span className="flex-1 text-muted-foreground truncate">{t.designation}</span>
                  <span className="font-mono text-foreground font-semibold">{fmtNum(t.quantite)} {t.unite}</span>
                </div>
              ))}
            </div>
          )}
        </Panel>

        {/* ============ Répartition par entrepôt ============ */}
        <Panel title="Répartition par entrepôt" testId="panel-ventilation" bodyClassName="p-3">
          {Object.keys(ventilParEntrepot).length === 0 ? <EmptyState compact title="Aucune ventilation" /> : (
            <div className="space-y-2">
              {Object.entries(ventilParEntrepot).map(([entrepot, lignes]) => (
                <div key={entrepot} data-testid={`ventil-${entrepot}`} className="border-b border-border/40 pb-1.5 last:border-0">
                  <div className="text-[11px] font-mono text-muted-foreground mb-0.5">{entrepot}</div>
                  <div className="flex flex-wrap gap-3 text-xs font-mono">
                    {lignes.map((l, i) => (
                      <span key={i}><b>{fmtNum(l.quantite)}</b> {l.unite}</span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Panel>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {/* ============ 5 derniers mouvements ============ */}
        <Panel title="5 derniers mouvements du jour" testId="panel-derniers-mvts" bodyClassName="p-3">
          {derniers.length === 0 ? <EmptyState compact title="Aucun mouvement aujourd'hui" /> : (
            <div className="space-y-1 text-[11px] font-mono">
              {derniers.map((m) => (
                <div key={m.id} data-testid={`mvt-recent-${m.id}`} className="flex items-center gap-2 py-1 border-b border-border/40 last:border-0">
                  <span className="text-muted-foreground w-14">{(m.horodatage || "").slice(11, 16)}</span>
                  <span className={m.quantite > 0 ? "text-emerald-400 w-20 text-right" : "text-rose-400 w-20 text-right"}>
                    {m.quantite > 0 ? "+" : ""}{fmtNum(m.quantite)} {m.unite}
                  </span>
                  <span className="flex-1 truncate">{m.produit}</span>
                  <span className="text-muted-foreground">{m.type}</span>
                </div>
              ))}
            </div>
          )}
        </Panel>

        {/* ============ Alertes stock (placeholder V2.1) ============ */}
        <Panel title="Alertes stock mini / maxi" testId="panel-alertes-stock" bodyClassName="p-3">
          <div className="flex items-start gap-2 text-xs text-muted-foreground">
            <BellRing className="h-4 w-4 text-amber-400 shrink-0 mt-0.5" />
            <div>
              <div className="font-semibold text-foreground mb-1">Disponible en V2.1</div>
              <p>{d.placeholder_alertes_stock?.message || "Les seuils de réapprovisionnement seront configurables depuis l'écran Produits dans la prochaine version."}</p>
            </div>
          </div>
        </Panel>
      </div>
    </div>
  );
};
